/**
 * JomBaca Tadika audio: what each tappable text should SOUND like, kept apart from what it LOOKS like.
 *
 * - Display text never changes ("ca" stays "ca" on screen).
 * - Every clip gets a pronunciation in IPA from a small, deterministic Malay G2P plus a lexicon
 *   that marks e pepet (ê → ə) and e taling (é → e). Any word with "e" MUST be in the lexicon
 *   (enforced by tests), because spelling alone cannot tell them apart.
 * - The audio file key includes a hash of that pronunciation, so fixing a pronunciation creates a
 *   new file name and old cached audio can never be played for it.
 */
import { KATEGORI } from "./tadikaKategori.js";
import { tadikaEnTexts } from "./tadikaEn.js";
import { BACA_LEVELS_EN, bacaEnLevelTexts } from "./tadikaBacaEn.js";
import { tadikaGameEnTexts } from "./tadikaGamesEn.js";
import { BACA_LEVELS, type BacaLevel } from "./tadika.js";
import { JAWI_LETTERS, tadikaGameTexts, type JawiLetter } from "./tadikaGames.js";
import { sainsTexts, spoken } from "./sainsInteraktif.js";

export type ClipKind = "syl" | "word" | "line" | "drill" | "jawi";
export type BacaClip = { key: string; kind: ClipKind; text: string; pron: string; ipa: string; language?: "ar" | "ms-MY" | "en-GB"; letterId?: string; ttsText?: string };

/**
 * Open-syllable drills (be, ce, de…) are taught with e pepet ([ə]) — the e of "beli", "kecil".
 * Needs confirmation by a Malay preschool teacher; flip here if drills should use e taling.
 */
export const DRILL_E: "pepet" | "taling" = "pepet";

/** Words containing "e": ê = e pepet [ə], é = e taling [e]. Other letters as written. */
export const E_LEXICON: Record<string, string> = {
  // syllables used in drills / tiles that stand for a specific word sound
  khe: "khé", kek: "kék",
  // names of the category items (Buah, Kenderaan, Pekerjaan)
  belimbing: "bêlimbing", ceri: "céri", delima: "dêlima", lemon: "lémon", teksi: "téksi", jentolak: "jêntolak", feri: "féri", roket: "rokét", skuter: "skutêr",
  posmen: "posmén", juruterbang: "jurutêrbang", jurutera: "jurutêra", pengawal: "pêngawal", pelukis: "pêlukis", penyanyi: "pênyanyi", keselamatan: "kêsêlamatan",
  beruang: "bêruang", penguin: "pênguin", lebah: "lêbah",
  // words and sentence tokens
  beli: "bêli", bendera: "bêndéra", "bersama-sama": "bêrsama-sama", comel: "comél", dengan: "dêngan",
  kamera: "kaméra", ke: "kê", kecil: "kêcil", kedai: "kêdai", kelapa: "kêlapa", kelawar: "kêlawar",
  keluar: "kêluar", keluarga: "kêluarga", kemeja: "kêméja", kepada: "kêpada", kerang: "kêrang",
  kerbau: "kêrbau", kereta: "kêréta", kerusi: "kêrusi", khemah: "khémah", komputer: "komputêr",
  merah: "mérah", mereka: "mêréka", oren: "orén", penyu: "pênyu", pergi: "pêrgi", petang: "pêtang",
  petik: "pêtik", sate: "saté", sebatang: "sêbatang", seekor: "sêékor", sekolah: "sêkolah",
  telefon: "téléfon", telur: "têlur", terima: "têrima",
  // Tadika games (Kira Gambar, Mewarna, Celik Jawi)
  ekor: "ékor", epal: "épal", pensel: "pénsêl", semuanya: "sêmuanya", betul: "bêtul", empat: "êmpat",
  enam: "ênam", sembilan: "sêmbilan", sepuluh: "sêpuluh", kelabu: "kêlabu", lembu: "lêmbu", ye: "yé", berapa: "bêrapa",
  // Tadika categories (remake): standard Malaysian pronunciation. Spelling that differs from the sound is marked here only.
  meja: "méja", belon: "bêlon", segi: "sêgi", tepat: "têpat", kepala: "kêpala", telinga: "têlinga", monyet: "monyét", tembikai: "têmbikai",
  strawberi: "strawbêri", betik: "bêtik", goreng: "goréng", terbang: "têrbang", helikopter: "hélikoptér", pelangi: "pêlangi", petani: "pêtani",
  pemandu: "pêmandu", nelayan: "nêlayan", gembira: "gêmbira", sedih: "sêdih", terkejut: "têrkêjut", mengantuk: "mêngantuk", nenas: "nênas",
  sentuh: "sêntuh", bentuk: "bêntuk",
  // Sains games (Perjalanan Makanan, Sistem Suria). Standard Malaysian pronunciation; to be confirmed by a Malay teacher at review.
  air: "a-ir",
  atmosfera: "atmosféra", atmosferanya: "atmosféranya", beberapa: "bêbêrapa", belas: "bêlas", berada: "bêrada", berapakah: "bêrapakah", berapi: "bêrapi",
  berbatu: "bêrbatu", berdebu: "bêrdêbu", beredar: "bêrêdar", bergolek: "bêrgolék", berkarat: "bêrkarat", berlaku: "bêrlaku", berlingkar: "bêrlingkar",
  bernafas: "bêrnafas", berputar: "bêrputar", berwarna: "bêrwarna", besar: "bêsar", besi: "bési", cecair: "cêcair", celcius: "sélsius", dekat: "dékat",
  dicerna: "dicêrna", dikeluarkan: "dikêluarkan", dikenali: "dikênali", diperbuat: "dipêrbuat", diperlukan: "dipêrlukan", diserap: "disêrap", ditelan: "ditêlan",
  edaran: "êdaran", edarannya: "êdarannya", esofagus: "ésofagus", helium: "hélium", hidrogen: "hidrogén", kebanyakannya: "kêbanyakannya", kedua: "kêdua",
  keempat: "kêêmpat", keenam: "kêênam", kehijauan: "kêhijauan", kelapan: "kêlapan", kelihatan: "kêlihatan", kelima: "kêlima", kencang: "kêncang",
  kepingan: "kêpingan", kerana: "kêrana", ketiga: "kêtiga", ketujuh: "kêtujuh", ketumpatannya: "kêtumpatannya", lebih: "lêbih", melalui: "mêlalui",
  melembutkan: "mêlêmbutkan", membawa: "mêmbawa", memberi: "mêmbêri", memerangkap: "mêmêrangkap", mempunyai: "mêmpunyai", mengambil: "mêngambil",
  mengandungi: "mêngandungi", mengelilingi: "mêngêlilingi", menghancurkan: "mênghancurkan", mengikut: "mêngikut", mengiring: "mêngiring", mengisar: "mêngisar",
  mengunyah: "mêngunyah", menjadi: "mênjadi", menjaga: "mênjaga", menolak: "mênolak", menuju: "mênuju", menyambung: "mênyambung", menyerap: "mênyêrap",
  menyimpan: "mênyimpan", metana: "métana", negatif: "négatif", neptun: "néptun", oksigen: "oksigén", oleh: "oléh", pekat: "pêkat", penghujung: "pênghujung",
  penuh: "pênuh", pepejal: "pêpêjal", peratus: "pêratus", perjalanan: "pêrjalanan", permukaan: "pêrmukaan", permukaannya: "pêrmukaannya", pertama: "pêrtama",
  perut: "pêrut", planet: "planét", sebagai: "sêbagai", secara: "sêcara", sedut: "sêdut", sejuk: "sêjuk", selepas: "sêlêpas", seluruh: "sêluruh",
  semakin: "sêmakin", semua: "sêmua", seperti: "sêpêrti", seratus: "sêratus", sesuai: "sêsuai", seterusnya: "sêtêrusnya", setiap: "sêtiap", sistem: "sistêm",
  tebal: "têbal", tempat: "têmpat", tengah: "têngah", terang: "têrang", terdiri: "têrdiri", terkenal: "têrkênal", terlalu: "têrlalu", tertinggi: "têrtinggi",
  terutamanya: "têrutamanya", tetapi: "têtapi",
  // Sains: fotosintesis
  fotosintesis: "fotosintésis", membuat: "mêmbuat", sendiri: "sêndiri", menangkap: "mênangkap", tenaga: "tênaga", menukar: "mênukar",
  dilepaskan: "dilêpaskan", semasa: "sêmasa", sumber: "sumbêr", mengapakah: "mêngapakah",
};

const DIGRAPH: [string, string][] = [["ng", "ŋ"], ["ny", "ɲ"], ["sy", "ʃ"], ["kh", "x"], ["gh", "ɣ"]];
const SINGLE: Record<string, string> = {
  a: "a", i: "i", o: "o", u: "u", "ê": "ə", "é": "e",
  b: "b", c: "tʃ", d: "d", f: "f", g: "ɡ", h: "h", j: "dʒ", k: "k", l: "l", m: "m", n: "n",
  p: "p", q: "k", r: "r", s: "s", t: "t", v: "v", w: "w", x: "ks", y: "j", z: "z",
};
const DIPHTHONG: Record<string, string> = { ai: "aj", au: "aw", oi: "oj" };

/** Spelling with e marked (ê/é). Throws if a word with a plain "e" is not in the lexicon. */
export function markE(token: string, drill = false): string {
  const t = token.toLowerCase();
  if (E_LEXICON[t]) return E_LEXICON[t]!;
  if (!t.includes("e")) return t;
  if (drill && /^[a-z]{0,2}e$/.test(t)) return t.replace(/e$/, DRILL_E === "pepet" ? "ê" : "é");
  throw new Error(`e pepet/taling unknown for "${token}" — add it to E_LEXICON`);
}

/** Deterministic Malay G2P for one marked token (no spaces). */
export function tokenIpa(marked: string): string {
  const parts = marked.split("-").filter(Boolean);
  return parts.map((p) => {
    let out = "";
    for (let i = 0; i < p.length; ) {
      const rest = p.slice(i);
      // word-final diphthong (pantai, pisau, dodoi) — but not "main" (ma-in) or "buah" (bu-ah)
      const dip = Object.keys(DIPHTHONG).find((d) => rest === d);
      if (dip) { out += DIPHTHONG[dip]; break; }
      const dg = DIGRAPH.find(([g]) => rest.startsWith(g));
      if (dg) { out += dg[1]; i += dg[0].length; continue; }
      const ch = p[i]!;
      if (ch === "k" && i === p.length - 1 && p.length > 1) { out += "ʔ"; i++; continue; } // final -k = glottal stop (katak, kek)
      const v = SINGLE[ch];
      if (v === undefined) throw new Error(`no sound for "${ch}" in "${marked}"`);
      out += v;
      i++;
    }
    return out;
  }).join(" ");
}

export function textIpa(text: string, drill = false): { pron: string; ipa: string } {
  const tokens = text.toLowerCase().replace(/[.,!?]/g, "").split(/\s+/).filter(Boolean);
  const marked = tokens.map((t) => markE(t, drill));
  return { pron: marked.join(" "), ipa: marked.map(tokenIpa).join(" ") };
}

/** FNV-1a 32-bit, hex — stable across runtimes. */
export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (const ch of new TextEncoder().encode(s)) { h ^= ch; h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, "0");
}

const slug = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

/** Arabic input is separate from the poster's exact Rumi labels. Never key by label: ح and ه are both ha. */
const JAWI_ARABIC: Record<string, [string, string]> = {
  "ا": ["أَلِف", "ʔalif"], "ب": ["بَاء", "baːʔ"], "ت": ["تَاء", "taːʔ"], "ث": ["ثَاء", "θaːʔ"],
  "ج": ["جِيم", "dʒiːm"], "ح": ["حَاء", "ħaːʔ"], "خ": ["خَاء", "xaːʔ"], "د": ["دَال", "daːl"],
  "ذ": ["ذَال", "ðaːl"], "ر": ["رَاء", "raːʔ"], "ز": ["زَاي", "zaːj"], "س": ["سِين", "siːn"],
  "ش": ["شِين", "ʃiːn"], "ص": ["صَاد", "sˤɑːd"], "ض": ["ضَاد", "dˤɑːd"], "ط": ["طَاء", "tˤɑːʔ"],
  "ظ": ["ظَاء", "ðˤɑːʔ"], "ع": ["عَيْن", "ʕajn"], "غ": ["غَيْن", "ɣajn"], "ف": ["فَاء", "faːʔ"],
  "ق": ["قَاف", "qaːf"], "ک": ["كَاف", "kaːf"], "ل": ["لَام", "laːm"], "م": ["مِيم", "miːm"],
  "ن": ["نُون", "nuːn"], "و": ["وَاو", "waːw"], "ه": ["هَاء", "haːʔ"], "لا": ["لَام أَلِف", "laːm ʔalif"],
  "ء": ["هَمْزَة", "hamza"], "ي": ["يَاء", "jaːʔ"],
};

export function jawiClip(letter: JawiLetter): BacaClip {
  const arabic = JAWI_ARABIC[letter.j];
  const language = arabic ? "ar" : "ms-MY";
  const ttsText = arabic?.[0] ?? letter.name;
  const ipa = arabic?.[1] ?? textIpa(letter.name).ipa;
  const identity = [...letter.j].map(c => c.codePointAt(0)!.toString(16)).join("-");
  return { key: `jawi-u${identity}-${fnv1a(`jawi|${letter.j}|${language}|${ipa}`)}`, kind: "jawi", text: letter.name,
    pron: ttsText, ipa, language, letterId: letter.j, ttsText };
}

/** `marked` = spelling with ê/é already decided (e.g. "té" taken from "téléfon"). */
export function clipFor(kind: ClipKind, text: string, marked?: string): BacaClip {
  const display = kind === "line" ? text.trim() : text.trim().toLowerCase().replace(/[.,!?]/g, "");
  const { pron, ipa } = textIpa(marked ?? display, kind === "syl");
  return { key: `${kind}-${slug(display)}-${fnv1a(`${kind}|${ipa}`)}`, kind, text: display, pron, ipa };
}

/** An English clip (read in British English). Never goes through the Malay e pepet/taling rules. */
export function enClipFor(kind: "word" | "line", text: string): BacaClip {
  const display = text.trim();
  return { key: `en-${kind}-${slug(display)}-${fnv1a(`en|${kind}|${display}`)}`, kind, text: display, pron: display, ipa: "", language: "en-GB" };
}

/** The clip for a Sains text in the chosen language (Malay texts are first written out as spoken Malay). */
export function sainsClip(kind: "word" | "line", text: string, lang: "ms" | "en"): BacaClip {
  return lang === "en" ? enClipFor(kind, spoken(text, "en")) : clipFor(kind, spoken(text, "ms"));
}

/** The syllables of a word, each sounding as it does inside that word ("te" in telefon = té). */
export function syllableClips(syllables: string[], word: string): BacaClip[] {
  const marked = markE(word.replace(/-/g, ""));
  let at = 0;
  return syllables.map((s) => { const m = marked.slice(at, at + s.length); at += s.length; return clipFor("syl", s, m); });
}

/** "ke, re, ta… kereta" as ONE clip (one tap = one file, so phones never block a second sound). */
export function drillClip(syllables: string[], word: string): BacaClip {
  const parts = syllableClips(syllables, word);
  const whole = clipFor("word", word.replace(/-/g, ""));
  const text = syllables.join("-");
  const ipa = `${parts.map((p) => p.ipa).join(" | ")} ‖ ${whole.ipa}`;
  return { key: `drill-${slug(text)}-${fnv1a(`drill|${ipa}`)}`, kind: "drill", text, pron: `${parts.map((p) => p.pron).join("-")} → ${whole.pron}`, ipa };
}

/** Every clip one level's pages can play. */
export function bacaLevelClips(l: BacaLevel): BacaClip[] {
  const out = new Map<string, BacaClip>();
  const add = (c: BacaClip) => out.set(c.key, c);
  for (const g of l.groups) for (const it of g.items) {
    if (it.includes("-")) { const w = it.replace(/-/g, ""); add(clipFor("word", w)); syllableClips(it.split("-"), w).forEach(add); add(drillClip(it.split("-"), w)); }
    else add(clipFor("syl", it));
  }
  for (const w of l.words) {
    add(clipFor("word", w.w));
    syllableClips(w.s, w.w).forEach(add);
    if (w.s.length > 1) add(drillClip(w.s, w.w));
  }
  const lines = [...l.sentences, ...(l.stories ?? []).flatMap((st) => [...st.lines, st.title, ...st.questions.map((q) => q.q)])];
  for (const line of lines) {
    add(clipFor("line", line));
    for (const tok of line.replace(/[.,!?]/g, "").split(/\s+/).filter(Boolean)) add(clipFor("word", tok));
  }
  return [...out.values()];
}

/** English Jom Baca: the clips one level plays. */
export function bacaLevelClipsEn(l: BacaLevel): BacaClip[] {
  const t = bacaEnLevelTexts(l);
  return [...t.words.map((x) => enClipFor("word", x)), ...t.lines.map((x) => enClipFor("line", x))];
}

/** Clips for the Tadika games (questions, numbers, colours, Jawi letter names). */
export const tadikaGameClips = (): BacaClip[] => [...tadikaGameTexts().map((t) => clipFor(t.kind, t.text)), ...JAWI_LETTERS.map(jawiClip)];

/** Every clip the Tadika pages can play, de-duplicated by key. */
export function bacaClips(): BacaClip[] {
  const out = new Map<string, BacaClip>();
  for (const l of BACA_LEVELS) for (const c of bacaLevelClips(l)) out.set(c.key, c);
  for (const c of tadikaGameClips()) out.set(c.key, c);
  for (const t of sainsTexts("en")) out.set(enClipFor(t.kind, t.text).key, enClipFor(t.kind, t.text));
  for (const l of BACA_LEVELS_EN) for (const c of bacaLevelClipsEn(l)) out.set(c.key, c);
  for (const g of tadikaGameEnTexts()) out.set(enClipFor(g.kind, g.text).key, enClipFor(g.kind, g.text));
  const te = tadikaEnTexts();
  for (const w of te.words) out.set(enClipFor("word", w).key, enClipFor("word", w));
  for (const l of te.lines) out.set(enClipFor("line", l).key, enClipFor("line", l));
  // Malay names of every category item (Haiwan, Buah, Kenderaan, Pekerjaan...) so BM has a recorded voice too, not only the device voice.
  for (const c of KATEGORI) if (c.id !== "huruf" && c.id !== "nombor") for (const i of c.items) { const k = clipFor("word", i.ms); out.set(k.key, k); }
  return [...out.values()];
}

/** Characters a cloud TTS would bill for, if every clip were generated once. */
export const bacaCharCount = () => bacaClips().reduce((n, c) => n + c.text.length, 0);

